import React, { useState, useEffect } from 'react';
import { connect, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify'; 

import { getWatchlist, updateWatchlist, removeFromWatchlist } from '../../actions/ai-action';
import { getMarketIntelligence } from '../../actions/market-intelligence-action';
import MarketIntelligenceModal from '../MarketIntelligence/MarketIntelligenceModal';
import LockedFeature from '../MarketIntelligence/LockedFeature';
import { isPremiumOrPro, isPro } from '../../utils/SubUtils';
import './Newsfeed.css';

const SearchIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M21 21L15 15M17 10C17 13.866 13.866 17 10 17C6.13401 17 3 13.866 3 10C3 6.13401 6.13401 3 10 3C13.866 3 17 6.13401 17 10Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
);

const CloseIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
);

const Newsfeed = ({ getWatchlist, updateWatchlist, removeFromWatchlist }) => {
    const { t } = useTranslation();
    const [searchQuery, setSearchQuery] = useState('');
    
    // State for Market Intelligence Modal
    const [showMarketModal, setShowMarketModal] = useState(false);
    const [miPairId, setMiPairId] = useState(null);
    
    // Selectors
    // newsPairs = All Available Pairs (from "news_pairs" in API)
    const newsPairs = useSelector((state) => state.marketIntelligence.newsPairs) || [];
    // userWatchlist = User's Selected Pairs (from "watch_list" in API)
    const userWatchlist = useSelector((state) => state.marketIntelligence.userWatchlist) || [];
    
    const user = useSelector((state) => state.auth.user);
    const subscription = user?.user_subscription;

    const [isFocused, setIsFocused] = useState(false);

    useEffect(() => {
        getWatchlist();
    }, [getWatchlist]);

    const handleSearch = (e) => {
        setSearchQuery(e.target.value);
    };

    const removeStock = (e, stock) => {
        e.stopPropagation(); // Prevent opening modal when removing
        // Use ID (watchlist ID) to remove
        removeFromWatchlist(stock.pair_id).then(() => {
            toast.success("Stock removed from watchlist");
        }).catch(() => {
            toast.error("Failed to remove stock");
        });
    };

    const addStock = (stock) => {
        // Check limits
        const isPremiumUser = isPremiumOrPro(subscription) && !isPro(subscription);
        const limit = isPro(subscription) ? 50 : 15;
        
        if (userWatchlist.length >= limit) {
             toast.error(isPro(subscription)
                 ? "You have reached the maximum limit of 50 stocks." 
                 : "Free plan is limited to 15 stocks. Upgrade to Pro for more!"
             );
             return;
        }

        if (userWatchlist.find(s => s.pair_id === stock.pair_id)) {
            toast.info("Stock already in your watchlist");
            return;
        }

        updateWatchlist(stock.pair_id).then(() => {
             toast.success("Stock added to watchlist");
        }).catch(() => {
             toast.error("Failed to add stock");
        });

        setSearchQuery(''); 
        setIsFocused(false);
    };

    const handleStockClick = (stock) => {
        if (stock.pair_id) {
            setMiPairId(stock.pair_id);
            setShowMarketModal(true);
        }
    };

    const filteredStocks = searchQuery 
        ? newsPairs.filter(stock => 
            (stock.symbol || stock.name || "").toLowerCase().includes(searchQuery.toLowerCase()) || 
            (stock.company || "").toLowerCase().includes(searchQuery.toLowerCase())
          ).filter(stock => !userWatchlist.find(s => s.pair_id === stock.pair_id))
          .slice(0, 10) 
        : [];

    if (!isPremiumOrPro(subscription)) {
        return <LockedFeature title="Agentic News Feed" description="Track your favorite stocks and receive daily news updates. Available for Premium and Pro plans." />;
    }

    return (
        <div className="newsfeed-container">
            {/* Plan Banner */}
            {!isPro(subscription) && (
                <div className="plan-banner">
                    <span className="plan-text">
                        <b>Premium Plan:</b> Track up to 15 stocks.
                    </span>
                    <span className="upgrade-link">
                        Upgrade to Pro for 50 stocks + sentiment analysis
                    </span>
                </div>
            )}

            {/* Manage Section */}
            <div className="manage-section">
                <h4>Manage Stock Symbols</h4>
                <p className="manage-subtitle">
                    Add stocks to receive personalized news updates ({userWatchlist.length}/{isPremiumOrPro(subscription) ? 50 : 15} used). You will receive a daily email containing the latest developments for your basket of stocks.
                </p>

                {/* Search Bar */}
                <div className="search-wrapper">
                    <div className="search-input-container">
                        <div className="search-icon">
                            <SearchIcon />
                        </div>
                        <input 
                            type="text" 
                            className="stock-search-input" 
                            placeholder="Search stock symbols"
                            value={searchQuery}
                            onChange={handleSearch}
                            onFocus={() => setIsFocused(true)}
                            onBlur={() => setTimeout(() => setIsFocused(false), 200)}
                        />
                    </div>

                    {/* Search Results Dropdown - Visible when searching */}
                    {isFocused && searchQuery && (
                        <div className="search-results-list">
                            {filteredStocks.length > 0 ? filteredStocks.map(stock => (
                                <div key={stock.pair_id} className="search-result-item" onClick={() => addStock(stock)} style={{cursor: 'pointer'}}>
                                    <div className="stock-info">
                                        <img src={stock.icon || stock.logo} alt={stock.symbol} className="stock-logo" onError={(e) => {e.target.style.display='none'}} />
                                        <div className="stock-name-group">
                                            <span className="stock-symbol-text">
                                                {stock.symbol || stock.name} <span className="stock-company-name">({stock.company || stock.name})</span>
                                            </span>
                                        </div>
                                    </div>
                                    <span className="add-btn-badge">
                                        Add
                                    </span>
                                </div>
                            )) : (
                                <div style={{padding: '12px 16px', color: '#6B7280', fontSize: '14px'}}>
                                    No results found. Please email <a href="mailto:admin@stockhero.ai">admin@stockhero.ai</a> to get this stock added to Newsfeed.
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Current List (Only show if not searching or if we want to show it always, design implies always shown below) */}
                {(!isFocused || !searchQuery) && userWatchlist.length > 0 ? (
                    <div>
                        <h5 className="current-list-title">Current list of stock symbols</h5>
                        <div className="stock-list">
                            {userWatchlist.map(stock => (
                                <div 
                                    key={stock.id} 
                                    className="stock-chip" 
                                    onClick={() => handleStockClick(stock)}
                                    style={{cursor: 'pointer'}}
                                >
                                    <img src={stock.icon || stock.logo} alt={stock.name} className="stock-logo" onError={(e) => {e.target.style.display='none'}} />
                                    <span className="stock-symbol">{stock.name}</span>
                                    <button className="remove-stock-btn" onClick={(e) => removeStock(e, stock)}>
                                        <CloseIcon />
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                ) : null}
            </div>

            {/* Market Intelligence Modal */}
            <MarketIntelligenceModal
                isOpen={showMarketModal}
                close={() => setShowMarketModal(false)}
                defaultPairId={miPairId}
            />
        </div>
    );
};

export default connect(null, { getWatchlist, updateWatchlist, removeFromWatchlist })(Newsfeed);
